import { useState, useEffect } from "react";
import { ZSL, RawCanvas } from "@zapier/canvas-state";
import { useComposer } from "@zapier/agent-system";
import { useSetAtom } from "jotai";
import { nodesAtom, edgesAtom, zslAtom } from "@zapier/canvas-state";

// Type for saved chat state from localStorage
export type SavedChatState = {
  id: string;
  title: string;
  timestamp: number;
  messages: any[];
  additionalData: any;
  canvas: Partial<RawCanvas>;
  nodes: any[];
  edges: any[];
  zsl?: ZSL;
};

// Local storage key for saved chat states
const SAVED_CHAT_STATES_KEY = "zapier-saved-chat-states";

export function useSavedChatState(chatId?: string) {
  const [hasAppliedState, setHasAppliedState] = useState(false);
  const composerService = useComposer();

  // Atom setters for updating saved state
  const setNodes = useSetAtom(nodesAtom);
  const setEdges = useSetAtom(edgesAtom);
  const setZsl = useSetAtom(zslAtom);

  // Load and apply saved chat state if chatId is provided - only once on mount
  useEffect(() => {
    if (!chatId || !composerService || hasAppliedState) {
      return;
    }

    try {
      const savedData = localStorage.getItem(SAVED_CHAT_STATES_KEY);

      if (savedData) {
        const savedStates = JSON.parse(savedData);

        if (Array.isArray(savedStates)) {
          const savedState = savedStates.find((state) => state.id === chatId);

          if (savedState) {
            // Update chat state
            composerService.setMessages(savedState.messages);
            if (savedState.additionalData) {
              composerService.setAdditionalData(savedState.additionalData);
            }

            // Update canvas state atoms from the saved state
            if (savedState.nodes) {
              setNodes(savedState.nodes);
            }

            if (savedState.edges) {
              setEdges(savedState.edges);
            }

            if (savedState.zsl) {
              setZsl(savedState.zsl);
            }

            // Mark as applied so we don't override user changes
            setHasAppliedState(true);
          }
        }
      }
    } catch (error) {
      console.error(
        "[useSavedChatState] Failed to load saved chat state:",
        error,
      );
    }
  }, [chatId, composerService, hasAppliedState, setNodes, setEdges, setZsl]);
}
